/**
 * Orden de compra (spec 21; respuestas de la dueña del 27-10-2026): pedido de cotización al proveedor con productos y
 * cantidades, sin precios. Propone lo que está bajo el mínimo de la sucursal y de cada tienda o depósito: se marca lo
 * que se le pide a ESTE proveedor (no todo es del mismo) y lo demás sigue saliendo como sugerido para otra orden.
 * Sin entregas se edita; desde aquí se imprime o se guarda en PDF, se envía por correo al proveedor, se pasa a compra
 * (que se edita antes de confirmar) o se anula. El proveedor puede entregar por partes (aclaración de la dueña del
 * 27-10): cada entrega es una compra desde la orden, que muestra lo pedido, lo recibido y lo pendiente de cada producto
 * hasta recibirse completa; si el resto no va a llegar, se cierra lo pendiente. Si el proveedor no está en la lista, se
 * crea ahí mismo con el diálogo de proveedor de Compras y queda elegido.
 */
import { ESTADO_ORDEN_COMPRA_ETIQUETA, ORDEN_RECIBE, formatearCantidad, Money, UNIDAD_MEDIDA_ETIQUETA, type AlmacenDTO, type OrdenCompraInput, type SucursalDTO, type ProveedorDTO, type SugeridoCompraDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Ban, FileInput, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useTemaAyuda } from '@/ayuda/contexto.tsx';
import { EnviarCorreo } from '@/componentes/EnviarCorreo.tsx';
import { EstadoQuery } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select, Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { TituloPagina } from '@/layout/AppLayout.tsx';
import { get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { useProductos } from '@/modulos/productos/api.ts';
import { useAnularOrdenCompra, useGuardarOrdenCompra, useOrdenCompra, useProveedores, useSugeridosCompra } from './api.ts';
import { ProveedorDialog } from './Compras.tsx';

type SucursalMia = SucursalDTO & { almacenes: AlmacenDTO[] };
interface Fila {
  producto_id: string;
  codigo: string;
  nombre: string;
  unidad: string;
  cantidad: string;
  nota: string;
}
const cant = (v: string) => formatearCantidad(Money.parse(v));
const unidad = (u: string) => UNIDAD_MEDIDA_ETIQUETA[u as keyof typeof UNIDAD_MEDIDA_ETIQUETA] ?? u;
const ESTADO_V = { abierta: 'pendiente', parcial: 'pendiente', completa: 'ok', cerrada: 'neutro', anulada: 'critico' } as const;
const ESTADO_COMPRA = { borrador: 'borrador', confirmada: 'confirmada', anulada: 'anulada' } as const;
/** Opción del desplegable de proveedores que abre el diálogo para crear uno nuevo (no es un proveedor). */
const NUEVO_PROVEEDOR = '__nuevo__';

export function EditorOrdenCompra() {
  useTemaAyuda('compras-ordenes');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { puede } = useAuth();
  const puedeEditar = puede('compras.registrar');
  const orden = useOrdenCompra(id ?? null);
  const guardar = useGuardarOrdenCompra();
  const anular = useAnularOrdenCompra();
  const mia = useQuery({ queryKey: claves.sucursalMia, queryFn: () => get<SucursalMia | null>('/sucursales/mia') });
  const proveedores = useProveedores({ activo: 'true', limite: 100 });
  const [proveedorId, setProveedorId] = useState('');
  const [almacenId, setAlmacenId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [filas, setFilas] = useState<Fila[]>([]);
  const [hidratada, setHidratada] = useState(false);
  const [qProd, setQProd] = useState('');
  const productos = useProductos({ q: qProd || undefined, pagina: 1, limite: 8, activo: 'true' });
  const [qSug, setQSug] = useState('');
  const [marcados, setMarcados] = useState<Record<string, boolean>>({});
  const [anulando, setAnulando] = useState(false);
  // Proveedor creado desde aquí: se elige al instante, aunque la lista todavía no lo traiga
  const [nuevoProv, setNuevoProv] = useState(false);
  const [creado, setCreado] = useState<ProveedorDTO | null>(null);
  const [motivo, setMotivo] = useState('');

  const o = orden.data;
  // Se edita mientras no tenga entregas; recibe entregas abierta o recibida en parte
  const editable = puedeEditar && (!id || (o?.estado === 'abierta' && o.entregas === 0));
  const recibe = !!o && ORDEN_RECIBE.includes(o.estado);
  const cerrar = !!o && o.entregas > 0;
  const deLaOrden = new Map((o?.lineas ?? []).map((l) => [l.producto_id, l]));
  const conEntregas = !editable && !!o && o.entregas > 0;
  const sugeridos = useSugeridosCompra(qSug, id ?? null, editable);

  // Cargar la orden existente una sola vez; una nueva propone el almacén principal
  useEffect(() => {
    if (!id || !o || hidratada) return;
    const t = setTimeout(() => {
      setProveedorId(o.proveedor_id);
      setAlmacenId(o.almacen_id ?? '');
      setObservaciones(o.observaciones ?? '');
      setFilas(o.lineas.map((l) => ({ producto_id: l.producto_id, codigo: l.producto_codigo, nombre: l.producto_nombre, unidad: l.unidad, cantidad: Money.parse(l.cantidad).toDisplay(), nota: l.nota ?? '' })));
      setHidratada(true);
    }, 0);
    return () => clearTimeout(t);
  }, [id, o, hidratada]);
  useEffect(() => {
    if (id || almacenId || !mia.data) return;
    const t = setTimeout(() => setAlmacenId(mia.data?.almacenes.find((a) => a.principal)?.id ?? ''), 0);
    return () => clearTimeout(t);
  }, [id, almacenId, mia.data]);

  const agregar = (p: { id: string; codigo: string; nombre: string; unidad: string }, cantidad = '1') =>
    setFilas((f) => (f.some((x) => x.producto_id === p.id) ? f.map((x) => (x.producto_id === p.id ? { ...x, cantidad } : x)) : [...f, { producto_id: p.id, codigo: p.codigo, nombre: p.nombre, unidad: p.unidad, cantidad, nota: '' }]));
  const agregarMarcados = (lista: SugeridoCompraDTO[]) => {
    const elegidos = lista.filter((s) => marcados[s.producto_id]);
    for (const s of elegidos) agregar({ id: s.producto_id, codigo: s.producto_codigo, nombre: s.producto_nombre, unidad: s.unidad }, Money.parse(s.sugerido).toDisplay());
    setMarcados({});
    if (elegidos.length > 0) toast.success(`${elegidos.length} producto(s) agregados con la cantidad sugerida: ajústala si hace falta`);
  };
  const cambiar = (i: number, campo: 'cantidad' | 'nota', valor: string) => setFilas((f) => f.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));

  const datos = (): OrdenCompraInput | null => {
    if (!proveedorId) return null;
    if (filas.length === 0 || filas.some((f) => !parseMonto(f.cantidad)?.esPositivo())) return null;
    return { proveedor_id: proveedorId, almacen_id: almacenId || null, ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}), lineas: filas.map((f) => ({ producto_id: f.producto_id, cantidad: normalizarDecimal(f.cantidad), ...(f.nota.trim() ? { nota: f.nota.trim() } : {}) })) };
  };
  const listo = !!datos();
  const onGuardar = () => {
    const d = datos();
    if (!d) return;
    guardar.mutate(
      { id: id ?? null, datos: d },
      {
        onSuccess: (r) => {
          toast.success(id ? `Orden ${r.numero_formateado} guardada` : `Orden ${r.numero_formateado} creada`);
          if (!id) navigate(`/compras/ordenes/${r.id}`, { replace: true });
        },
        onError: (e) => toast.error('No se pudo guardar la orden', { description: mensajeDeError(e) }),
      },
    );
  };

  if (id && orden.isLoading) return <EstadoQuery cargando error={null} datos={null}>{() => null}</EstadoQuery>;
  if (id && orden.error) return <EstadoQuery cargando={false} error={orden.error} datos={null} reintentar={() => void orden.refetch()}>{() => null}</EstadoQuery>;
  const listaSug = sugeridos.data ?? [];
  const listaProv = [...(proveedores.data?.datos ?? []), ...(creado && !proveedores.data?.datos.some((p) => p.id === creado.id) ? [creado] : [])];
  const proveedorElegido = listaProv.find((p) => p.id === proveedorId);

  return (
    <>
      <TituloPagina
        titulo={o ? `Orden de compra ${o.numero_formateado}` : 'Nueva orden de compra'}
        subtitulo="Pedido de cotización al proveedor: productos y cantidades, sin precios. No mueve inventario ni cuentas por pagar: eso lo hace la compra."
        acciones={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" asChild><Link to="/compras?tab=ordenes"><ArrowLeft /> Órdenes</Link></Button>
            {o ? (
              <>
                <Button variant="outline" onClick={() => window.open(`/imprimir/OC/${o.id}`, '_blank')} data-testid="oc-imprimir"><Printer /> Imprimir o PDF</Button>
                {o.estado !== 'anulada' ? <EnviarCorreo tipo="orden_compra" id={o.id} /> : null}
                {recibe && puedeEditar ? (
                  <>
                    {/* La entrega llega con factura o con nota de entrega del proveedor (sin factura): la orden cuenta lo recibido igual */}
                    <Button asChild data-testid="oc-pasar-compra"><Link to={`/compras/nueva?orden=${o.id}`}><FileInput /> {o.entregas > 0 ? 'Recibir otra entrega' : 'Pasar a compra'} con factura</Link></Button>
                    <Button variant="outline" asChild data-testid="oc-pasar-nota"><Link to={`/compras/nueva?orden=${o.id}&tipo=nota`}><FileInput /> Con nota de entrega</Link></Button>
                    <Button variant="ghost" onClick={() => setAnulando(true)} data-testid="oc-anular"><Ban /> {cerrar ? 'Cerrar lo pendiente' : 'Anular'}</Button>
                  </>
                ) : null}
              </>
            ) : null}
            <AyudaVentana tema="compras-ordenes" />
          </div>
        }
      />
      {o ? (
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="oc-estado">
          <Badge variant={ESTADO_V[o.estado]}>{ESTADO_ORDEN_COMPRA_ETIQUETA[o.estado]}</Badge>
          <span className="text-muted-foreground">Creada {formatearFechaHora(o.creado_en)} por {o.creado_por_nombre}</span>
          {o.enviada_en ? <span className="text-muted-foreground" data-testid="oc-enviada">· enviada a {o.enviada_a} el {formatearFechaHora(o.enviada_en)}</span> : null}
          {o.estado === 'anulada' ? <span className="text-mg-rojo">· anulada: {o.motivo_anulacion}</span> : null}
          {o.estado === 'cerrada' ? <span className="text-muted-foreground" data-testid="oc-cerrada">· cerrada por {o.anulada_por_nombre} el {o.anulada_en ? formatearFechaHora(o.anulada_en) : ''}: {o.motivo_anulacion}</span> : null}
        </div>
      ) : null}

      <div className={`grid gap-4 ${editable ? 'xl:grid-cols-[1fr_24rem]' : ''}`}>
        <section className="space-y-3">
          <div className="grid gap-3 rounded-2xl border bg-card p-3 md:grid-cols-2">
            <Campo etiqueta="Proveedor" htmlFor="oc-proveedor">
              {editable ? (
                <div className="flex gap-2">
                  <Select id="oc-proveedor" value={proveedorId} onChange={(e) => (e.target.value === NUEVO_PROVEEDOR ? setNuevoProv(true) : setProveedorId(e.target.value))} data-testid="oc-proveedor">
                    <option value="">Elige el proveedor…</option>
                    {listaProv.map((p) => <option key={p.id} value={p.id}>{p.razon_social} · {p.rif}</option>)}
                    <option value={NUEVO_PROVEEDOR}>+ Nuevo proveedor (no está en la lista)…</option>
                  </Select>
                  <Button variant="outline" size="sm" className="h-10 shrink-0" onClick={() => setNuevoProv(true)} data-testid="oc-proveedor-nuevo"><Plus /> Nuevo</Button>
                </div>
              ) : <p className="text-sm font-medium">{o?.proveedor_razon_social} · {o?.proveedor_rif}</p>}
            </Campo>
            <Campo etiqueta="Debe llegar a" htmlFor="oc-almacen">
              {editable ? (
                <Select id="oc-almacen" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)} data-testid="oc-almacen">
                  <option value="">(sin indicar)</option>
                  {(mia.data?.almacenes ?? []).filter((a) => a.activo).map((a) => <option key={a.id} value={a.id}>{a.nombre}{a.principal ? ' (principal)' : ''}</option>)}
                </Select>
              ) : <p className="text-sm">{o?.almacen_nombre ?? '(sin indicar)'}</p>}
            </Campo>
            <Campo etiqueta="Observaciones (opcional)" htmlFor="oc-obs" className="md:col-span-2">
              {editable ? <Textarea id="oc-obs" rows={2} maxLength={500} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Condiciones, fecha en que se necesita…" data-testid="oc-observaciones" /> : <p className="text-sm">{o?.observaciones || '—'}</p>}
            </Campo>
            {editable && proveedorElegido && !proveedorElegido.correo ? <p className="text-xs text-mg-ambar md:col-span-2" data-testid="oc-sin-correo">{proveedorElegido.razon_social} no tiene correo registrado: para enviarle la orden por correo, agrégalo en su ficha (Proveedores).</p> : null}
          </div>

          <div className="rounded-2xl border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b p-3">
              <p className="font-medium">Productos ({filas.length})</p>
              {editable ? (
                <div className="relative w-80">
                  <Input placeholder="Agregar producto: código o nombre…" value={qProd} onChange={(e) => setQProd(e.target.value)} data-testid="oc-buscar-producto" />
                  {qProd.trim() && productos.data ? (
                    <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border bg-card shadow-sm">
                      {productos.data.datos.filter((p) => p.tipo === 'producto').map((p) => (
                        <li key={p.id}>
                          <button type="button" className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted" onClick={() => { agregar({ id: p.id, codigo: p.codigo, nombre: p.nombre, unidad: p.unidad }); setQProd(''); }} data-testid={`oc-producto-${p.codigo}`}>
                            <b>{p.codigo}</b> · {p.nombre}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
            {filas.length === 0 ? (
              <p className="p-4 text-sm text-muted-foreground">{editable ? 'Agrega productos con el buscador o desde los sugeridos por el mínimo.' : 'Sin productos.'}</p>
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Código</TableHead><TableHead>Descripción</TableHead><TableHead className="text-right">{conEntregas ? 'Pedido' : 'Cantidad'}</TableHead>{conEntregas ? <><TableHead className="text-right">Recibido</TableHead><TableHead className="text-right">Pendiente</TableHead></> : null}<TableHead>Nota</TableHead>{editable ? <TableHead /> : null}</TableRow></TableHeader>
                <TableBody>
                  {filas.map((f, i) => (
                    <TableRow key={f.producto_id} data-testid={`oc-linea-${f.codigo}`}>
                      <TableCell className="font-medium">{f.codigo}</TableCell>
                      <TableCell>{f.nombre}</TableCell>
                      <TableCell className="text-right">
                        {editable ? <Input inputMode="decimal" value={f.cantidad} onChange={(e) => cambiar(i, 'cantidad', e.target.value)} className="ml-auto h-8 w-24 text-right" aria-label={`Cantidad de ${f.codigo}`} data-testid={`oc-cantidad-${f.codigo}`} /> : <span className="num">{cant(normalizarDecimal(f.cantidad))}</span>}
                        <span className="text-[11px] text-muted-foreground"> {unidad(f.unidad)}</span>
                      </TableCell>
                      {conEntregas ? (
                        <>
                          <TableCell className="text-right num" data-testid={`oc-recibido-${f.codigo}`}>{cant(deLaOrden.get(f.producto_id)?.recibido ?? '0')}</TableCell>
                          <TableCell className={`text-right num ${Money.parse(deLaOrden.get(f.producto_id)?.pendiente ?? '0').esPositivo() ? 'font-semibold text-mg-ambar' : 'text-muted-foreground'}`} data-testid={`oc-pendiente-${f.codigo}`}>{cant(deLaOrden.get(f.producto_id)?.pendiente ?? '0')}</TableCell>
                        </>
                      ) : null}
                      <TableCell>{editable ? <Input value={f.nota} maxLength={200} onChange={(e) => cambiar(i, 'nota', e.target.value)} className="h-8" placeholder="Opcional" /> : <span className="text-xs">{f.nota}</span>}</TableCell>
                      {editable ? <TableCell><Button size="icon" variant="ghost" onClick={() => setFilas((x) => x.filter((_, j) => j !== i))} aria-label={`Quitar ${f.codigo}`}><Trash2 className="h-4 w-4" /></Button></TableCell> : null}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          {o && o.compras.length > 0 ? (
            <div className="rounded-2xl border bg-card p-3 text-sm" data-testid="oc-entregas">
              <p className="mb-1 font-medium">Entregas recibidas ({o.entregas})</p>
              <ul className="space-y-0.5">
                {o.compras.map((c) => (
                  <li key={c.compra_id} className={c.estado === 'anulada' ? 'text-muted-foreground line-through' : ''} data-testid={`oc-entrega-${c.numero_factura}`}>
                    <Link className="underline" to={`/compras?ver=${c.compra_id}`}>{c.tipo_documento === 'nota_entrega' ? 'Nota de entrega' : 'Factura'} {c.numero_factura}</Link>{c.tipo_documento === 'nota_entrega' ? <Badge variant="pendiente" className="ml-1">Sin factura</Badge> : null} del {c.fecha_factura.slice(8, 10)}/{c.fecha_factura.slice(5, 7)}/{c.fecha_factura.slice(0, 4)} · compra {c.numero_formateado ?? '(borrador)'} · {ESTADO_COMPRA[c.estado]}
                  </li>
                ))}
              </ul>
              {o.compras.some((c) => c.estado === 'borrador') ? <p className="mt-1 text-xs text-muted-foreground">Una compra en borrador ya cuenta como recibida en la orden; la mercancía entra al inventario al confirmarla.</p> : null}
            </div>
          ) : null}
          {editable ? (
            <div className="flex justify-end">
              <Button onClick={onGuardar} disabled={!listo} cargando={guardar.isPending} data-testid="oc-guardar"><Save /> {id ? 'Guardar cambios' : 'Crear orden de compra'}</Button>
            </div>
          ) : null}
        </section>

        {editable ? (
          <aside className="space-y-2 rounded-2xl border bg-card p-3 text-sm" data-testid="oc-sugeridos">
            <p className="font-medium">Sugeridos por el mínimo</p>
            <p className="text-xs text-muted-foreground">Lo que está por debajo del mínimo de la sucursal o de cada tienda y depósito, menos lo que sigue pendiente en otras órdenes. Marca lo que le pides a este proveedor: lo demás sigue sugerido.</p>
            <Input placeholder="Buscar en los sugeridos…" value={qSug} onChange={(e) => setQSug(e.target.value)} className="h-8" data-testid="oc-sugeridos-buscar" />
            {sugeridos.isLoading ? <p className="text-xs text-muted-foreground">Calculando…</p> : listaSug.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="oc-sugeridos-vacio">Nada por debajo del mínimo.</p> : (
              <>
                <ul className="max-h-[28rem] space-y-1 overflow-auto">
                  {listaSug.map((s) => {
                    const ya = filas.some((f) => f.producto_id === s.producto_id);
                    return (
                      <li key={s.producto_id} className="rounded-lg border px-2 py-1.5" data-testid={`oc-sugerido-${s.producto_codigo}`}>
                        <label className="flex items-start gap-2">
                          <input type="checkbox" className="mt-1" checked={!!marcados[s.producto_id]} onChange={(e) => setMarcados((m) => ({ ...m, [s.producto_id]: e.target.checked }))} aria-label={`Marcar ${s.producto_codigo}`} />
                          <span className="min-w-0 flex-1">
                            <b>{s.producto_codigo}</b> · {s.producto_nombre}{ya ? <Badge variant="neutro" className="ml-1">en esta orden</Badge> : null}
                            <span className="block text-xs">Sugerido <b className="num">{cant(s.sugerido)}</b> {unidad(s.unidad)}{Money.parse(s.en_ordenes).esPositivo() ? <span className="text-muted-foreground"> (faltan {cant(s.falta)}; pendiente en otras órdenes {cant(s.en_ordenes)})</span> : null}</span>
                            {s.detalle.map((d) => <span key={`${d.almacen_id ?? 'suc'}`} className="block text-[11px] text-muted-foreground">{d.ubicacion}: disponible {cant(d.disponible)} · mínimo {cant(d.minimo)} · faltan {cant(d.falta)}</span>)}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                <Button size="sm" className="w-full" variant="outline" disabled={!Object.values(marcados).some(Boolean)} onClick={() => agregarMarcados(listaSug)} data-testid="oc-agregar-sugeridos"><Plus /> Agregar los marcados</Button>
              </>
            )}
          </aside>
        ) : null}
      </div>

      <ProveedorDialog abierto={nuevoProv} cerrar={() => setNuevoProv(false)} alCrear={(p) => { setCreado(p); setProveedorId(p.id); }} />
      <Dialog open={anulando} onOpenChange={(v) => { if (!v) { setAnulando(false); setMotivo(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{cerrar ? `Cerrar lo pendiente de ${o?.numero_formateado}` : `Anular ${o?.numero_formateado}`}</DialogTitle>
            <DialogDescription>{cerrar ? 'Lo recibido queda; se deja de esperar lo pendiente, que vuelve a salir en los sugeridos. Ya no se reciben más entregas de esta orden.' : 'Ya no se podrá editar ni pasar a una compra. Lo que pedía vuelve a salir en los sugeridos.'}</DialogDescription>
          </DialogHeader>
          <Campo etiqueta="Motivo" htmlFor="oc-motivo"><Input id="oc-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="oc-motivo" /></Campo>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnulando(false)}>Volver</Button>
            <Button variant="destructive" disabled={motivo.trim().length < 5} cargando={anular.isPending} onClick={() => o && anular.mutate({ id: o.id, motivo: motivo.trim() }, { onSuccess: () => { toast.success(cerrar ? `Orden ${o.numero_formateado} cerrada` : `Orden ${o.numero_formateado} anulada`); setAnulando(false); setMotivo(''); }, onError: (e) => toast.error(cerrar ? 'No se pudo cerrar' : 'No se pudo anular', { description: mensajeDeError(e) }) })} data-testid="oc-anular-confirmar">{cerrar ? 'Cerrar lo pendiente' : 'Anular'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
